/**
 * BlogPostPage - Individual blog post view
 *
 * Note: The dangerouslySetInnerHTML usage is safe here because:
 * 1. Content comes from our own blog posts (not user input)
 * 2. Content is defined in TypeScript files we control
 * 3. No external/untrusted content is rendered
 */

import { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Tag, Share2, Check } from "lucide-react";
import { useState } from "react";
import { SEO } from "../components/SEO";
import {
  AuthorCard,
  TableOfContents,
  CallToAction,
  BlogCard,
} from "../components/blog";
import { Badge, Button } from "@bcc/shared-ui";
import { getPostBySlug, getRelatedPosts } from "../content/blog";
import { CATEGORY_INFO } from "../content/blog/types";
import { SITE_URL } from "../seo/page-meta";

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [copied, setCopied] = useState(false);

  const post = useMemo(() => (slug ? getPostBySlug(slug) : undefined), [slug]);
  const relatedPosts = useMemo(
    () => (post ? getRelatedPosts(post, 3) : []),
    [post],
  );
  const categoryInfo = post ? CATEGORY_INFO[post.category] : undefined;

  // If no post found, redirect to blog
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${SITE_URL}/blog/${post.slug}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.description,
          url: `${SITE_URL}/blog/${post.slug}`,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      handleCopyLink();
    }
  };

  /**
   * Simple markdown to React renderer
   * SAFE: Content is from our own blog posts defined in TypeScript files
   */
  const renderContent = (content: string) => {
    const lines = content.split("\n\n");
    return lines.map((block, index) => {
      const trimmed = block.trim();

      // Heading 2
      if (trimmed.startsWith("## ")) {
        const text = trimmed.slice(3);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <h2
            key={index}
            id={id}
            className="text-2xl font-bold text-gray-900 mt-8 mb-4 scroll-mt-24"
          >
            {text}
          </h2>
        );
      }

      // Heading 3
      if (trimmed.startsWith("### ")) {
        const text = trimmed.slice(4);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <h3
            key={index}
            id={id}
            className="text-xl font-semibold text-gray-900 mt-6 mb-3 scroll-mt-24"
          >
            {text}
          </h3>
        );
      }

      // List items
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        const items = trimmed
          .split("\n")
          .map((item) => item.replace(/^[-*]\s*/, ""));
        return (
          <ul
            key={index}
            className="list-disc list-inside space-y-2 my-4 text-gray-700"
          >
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
      }

      // Numbered list
      if (/^\d+\.\s/.test(trimmed)) {
        const items = trimmed
          .split("\n")
          .map((item) => item.replace(/^\d+\.\s*/, ""));
        return (
          <ol
            key={index}
            className="list-decimal list-inside space-y-2 my-4 text-gray-700"
          >
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
        );
      }

      // Table (pipe-delimited markdown)
      if (trimmed.startsWith("|") && trimmed.includes("\n|")) {
        const rows = trimmed
          .split("\n")
          .filter((row) => !row.match(/^\|[\s-:|]+\|$/));
        const headerCells = rows[0]
          .split("|")
          .filter((c) => c.trim())
          .map((c) => c.trim());
        const bodyRows = rows.slice(1).map((row) =>
          row
            .split("|")
            .filter((c) => c.trim())
            .map((c) => c.trim()),
        );
        return (
          <div key={index} className="overflow-x-auto my-6">
            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {headerCells.map((cell, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-left font-semibold text-gray-900 border-b border-gray-200"
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => (
                  <tr
                    key={ri}
                    className={ri % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="px-4 py-3 text-gray-700 border-b border-gray-100"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      // Blockquote
      if (trimmed.startsWith("> ")) {
        const text = trimmed.slice(2);
        return (
          <blockquote
            key={index}
            className="border-l-4 border-teal-500 pl-4 my-4 italic text-gray-600"
          >
            {text}
          </blockquote>
        );
      }

      // Code block
      if (trimmed.startsWith("```")) {
        const code = trimmed.replace(/```\w*\n?/g, "").trim();
        return (
          <pre
            key={index}
            className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto my-4 text-sm"
          >
            <code>{code}</code>
          </pre>
        );
      }

      // Regular paragraph - render with inline formatting
      // Note: Safe to use innerHTML because content is from our own blog posts
      if (trimmed) {
        // Parse inline markdown: bold and inline code
        const parts: React.ReactNode[] = [];
        let remaining = trimmed;
        let key = 0;

        while (remaining.length > 0) {
          // Check for bold
          const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
          // Check for inline code
          const codeMatch = remaining.match(/`([^`]+)`/);

          if (!boldMatch && !codeMatch) {
            parts.push(remaining);
            break;
          }

          // Find which comes first
          const boldIndex = boldMatch
            ? remaining.indexOf(boldMatch[0])
            : Infinity;
          const codeIndex = codeMatch
            ? remaining.indexOf(codeMatch[0])
            : Infinity;

          if (boldIndex < codeIndex && boldMatch) {
            // Add text before bold
            if (boldIndex > 0) {
              parts.push(remaining.slice(0, boldIndex));
            }
            // Add bold text
            parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
            remaining = remaining.slice(boldIndex + boldMatch[0].length);
          } else if (codeMatch) {
            // Add text before code
            if (codeIndex > 0) {
              parts.push(remaining.slice(0, codeIndex));
            }
            // Add code text
            parts.push(
              <code
                key={key++}
                className="bg-gray-100 px-1 py-0.5 rounded text-sm text-teal-700"
              >
                {codeMatch[1]}
              </code>,
            );
            remaining = remaining.slice(codeIndex + codeMatch[0].length);
          }
        }

        return (
          <p key={index} className="text-gray-700 leading-relaxed my-4">
            {parts}
          </p>
        );
      }

      return null;
    });
  };

  return (
    <>
      <SEO
        title={`${post.title} | Builder Buddy Blog`}
        description={post.description}
        keywords={post.tags}
        ogType="article"
        articlePublishedTime={post.publishedAt}
        articleModifiedTime={post.updatedAt}
        articleAuthor="Builder Buddy Team"
        blogPost={{
          title: post.title,
          description: post.description,
          slug: post.slug,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt,
          authorName: "Builder Buddy Team",
          authorSlug: "builder-buddy-team",
          image: post.featuredImage,
          keywords: post.tags,
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
      />

      <div className="max-w-4xl mx-auto">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-600 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <header className="mb-8">
            {post.featuredImage && (
              <div className="aspect-video rounded-xl overflow-hidden mb-6 bg-gray-100">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              {categoryInfo && (
                <Badge variant="info">{categoryInfo.label}</Badge>
              )}
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {post.readingTime} min read
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>

            <p className="text-xl text-gray-600 mb-6">{post.description}</p>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-gray-200">
              <AuthorCard variant="compact" />
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formatDate(post.publishedAt)}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleShare}
                  className="flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Share2 className="h-4 w-4" />
                      Share
                    </>
                  )}
                </Button>
              </div>
            </div>
          </header>

          {/* Content layout with TOC sidebar on desktop */}
          <div className="flex gap-8">
            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Table of Contents (mobile - top) */}
              {post.tableOfContents.length > 0 && (
                <div className="lg:hidden mb-6">
                  <TableOfContents items={post.tableOfContents} />
                </div>
              )}

              {/* Article content */}
              <div className="prose prose-lg max-w-none">
                {renderContent(post.content)}
              </div>

              {/* Tags */}
              {post.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag className="h-4 w-4 text-gray-400" />
                    {post.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-gray-200 transition-colors"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Call to Action */}
              {post.callToAction && (
                <div className="mt-8">
                  <CallToAction cta={post.callToAction} variant="banner" />
                </div>
              )}

              {/* Author bio (full) */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  About the Author
                </h3>
                <AuthorCard variant="full" />
              </div>
            </div>

            {/* Sidebar with TOC (desktop only) */}
            {post.tableOfContents.length > 0 && (
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <TableOfContents items={post.tableOfContents} />
                </div>
              </aside>
            )}
          </div>
        </motion.article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <BlogCard
                  key={relatedPost.slug}
                  post={relatedPost}
                  variant="compact"
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default BlogPostPage;
